import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./adminEvent.css";

import AdminEventForm from "./AdminEventForm";

/**
 * IMPORTANT: import EVERYTHING for debug
 */
import * as AdminEventService from "./adminEventService";

import {
  fetchAllEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  cancelEvent,
  rescheduleEvent,
  generateZoomLink,
  fetchEventUsers
} from "./adminEventService";


const AdminEventPage = () => {

  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);


  /**
   * ================= DATE HANDLING HELPERS (ADDED)
   */

  const formatDateSafe = (dateValue) => {

    try {

      if (!dateValue) return "-";

      const date = new Date(dateValue);

      if (isNaN(date.getTime())) return dateValue;

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });

    } catch (error) {

      console.error("Date format error:", error);

      return dateValue;

    }

  };


const formatTimeSafe = (timeValue) => {

  try {

    if (!timeValue) return "-";

    // Parse UTC time
    const utcDate = new Date(timeValue);

    if (isNaN(utcDate.getTime())) return timeValue;

    // Convert to IST explicitly
    const istString = utcDate.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

    // Force uppercase AM/PM
    const formatted = istString
      .replace("am", "AM")
      .replace("pm", "PM");

    return `${formatted} IST`;

  } catch (error) {

    console.error("Time format error:", error);

    return timeValue;

  }

};


  /**
 * JOIN ZOOM MEETING
 */
const handleJoinZoom = (event) => {

  try {

    if (!event.zoomLink) {

      alert("Zoom link not available");
      return;

    }

    console.log("Joining Zoom:", event.zoomLink);

    window.open(event.zoomLink, "_blank", "noopener,noreferrer");

  } catch (error) {

    console.error("Join zoom error:", error);

    alert("Failed to open Zoom link");

  }

};


  /**
   * DEBUG: Check what is actually imported
   */
  useEffect(() => {

    console.log("========= ADMIN EVENT SERVICE DEBUG =========");

    console.log("Full Service Object:", AdminEventService);

    console.log("fetchAllEvents:", fetchAllEvents);
    console.log("createEvent:", createEvent);
    console.log("updateEvent:", updateEvent);
    console.log("deleteEvent:", deleteEvent);
    console.log("cancelEvent:", cancelEvent);
    console.log("rescheduleEvent:", rescheduleEvent);
    console.log("generateZoomLink:", generateZoomLink);
    console.log("fetchEventUsers:", fetchEventUsers);

    console.log("Type of fetchAllEvents:", typeof fetchAllEvents);

    console.log("=============================================");

    loadEvents();

  }, []);



  /**
   * LOAD EVENTS
   */
  const loadEvents = async () => {

    try {

      console.log("Calling fetchAllEvents...");

      setLoading(true);

      const safeFetchAllEvents =
        fetchAllEvents ||
        AdminEventService.fetchAllEvents ||
        AdminEventService.fetchAllEvents;

      if (!safeFetchAllEvents) {

        console.error("fetchAllEvents is UNDEFINED!");

        alert("fetchAllEvents is undefined. Check service import path.");

        setLoading(false);

        return;

      }

      const response = await safeFetchAllEvents();

      console.log("fetchAllEvents response:", response);

      if (response.status === 200) {

        console.log("Events loaded:", response.data);

        setEvents(response.data);

      } else {

        console.error("Failed response:", response);

        alert(response.message || "Failed to load events");

      }

    } catch (error) {

      console.error("loadEvents ERROR:", error);

      alert("Unexpected error loading events");

    } finally {

      setLoading(false);

    }

  };



  /**
   * DELETE EVENT
   */
  const handleDelete = async (id) => {

    try {

      console.log("Deleting event:", id);

      if (!window.confirm("Delete this event?")) return;

      const safeDeleteEvent =
        deleteEvent ||
        AdminEventService.deleteEvent ||
        AdminEventService.deleteEvent;

      const response = await safeDeleteEvent(id);

      console.log("Delete response:", response);

      if (response.status === 200 || response.status === 204) {

        loadEvents();

      } else {

        alert(response.message);

      }

    } catch (error) {

      console.error("Delete error:", error);

    }

  };



  /**
   * CANCEL EVENT
   */
  const handleCancel = async (id) => {

    try {

      console.log("Cancelling event:", id);

      if (!window.confirm("Cancel this event?")) return;

      const safeCancelEvent =
        cancelEvent ||
        AdminEventService.cancelEvent ||
        AdminEventService.cancelEvent;

      const response = await safeCancelEvent(id);

      console.log("Cancel response:", response);

      if (response.status === 200) {

        loadEvents();

      } else {

        alert(response.message);

      }

    } catch (error) {

      console.error("Cancel error:", error);

    }

  };



/**
 * ADD ZOOM LINK
 */
const handleAddZoom = async (event) => {

  try {

    console.log("Generating zoom for event:", event);

    if (!window.confirm("Generate Zoom link for this event?")) return;

    const safeGenerateZoom =
      generateZoomLink ||
      AdminEventService.generateZoomLink;

    if (!safeGenerateZoom) {

      alert("generateZoomLink function not found");
      return;

    }

    const response = await safeGenerateZoom(event.id);

    console.log("Generate Zoom response:", response);

    if (response.status === 201) {

      alert("Zoom link created successfully");

      loadEvents();

    } 
    else if (response.status === 200) {

      alert("Zoom link already exists");

      loadEvents();

    } 
    else {

      alert(response.message);

    }

  } catch (error) {

    console.error("Generate Zoom error:", error);

    alert("Failed to generate Zoom link");

  }

};



  return (

    <div className="admin-container">

      <div className="admin-header">

        <h2>Event Management</h2>

        <button onClick={() => {

          console.log("Opening create form");

          setEditing(null);

          setShowForm(true);

        }}>

          + New Event

        </button>

      </div>



      {showForm && (

        <AdminEventForm

          event={editing}

          onSaved={() => {

            console.log("Event saved");

            setShowForm(false);

            loadEvents();

          }}

          onCancel={() => {

            console.log("Form cancelled");

            setShowForm(false);

          }}

        />

      )}



      {loading ? (

        <p>Loading events...</p>

      ) : (

        <table className="event-table">

          <thead>

            <tr>

              <th>Title</th>
              <th>Description</th>
              <th>Date</th>
              <th>Start Time</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Actions</th>

            </tr>

          </thead>

          <tbody>

            {events.length === 0 ? (

              <tr>

                <td colSpan="6">No events found</td>

              </tr>

            ) : (

              events.map((e) => (

                <tr key={e.id}>

                  <td>{e.title}</td>
                 
                  <td>{e.description}</td>

                  <td>{formatDateSafe(e.eventDate)}</td>

                  <td>{formatTimeSafe(e.eventStartTime)}</td>

                  <td>{e.eventDuration} mins</td>

                  <td>

                    {e.cancelled ? (

                      <span className="status cancelled">Cancelled</span>

                    ) : (

                      <span className="status active">Active</span>

                    )}

                  </td>

                  <td className="actions">

                    <button onClick={() => {

                      console.log("Editing event:", e);

                      setEditing(e);

                      setShowForm(true);

                    }}>

                      Edit

                    </button>



                    {!e.cancelled && (

                      <button
                        className="warning"
                        onClick={() => handleCancel(e.id)}
                      >
                        Cancel
                      </button>

                    )}



                    {e.zoomLink ? (

  <button
    className="success"
    onClick={() => handleJoinZoom(e)}
  >
    Join
  </button>

) : (

  <button
    className="info"
    onClick={() => handleAddZoom(e)}
  >
    Add Zoom
  </button>

)}



                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(e.id)}
                    >
                      Delete
                    </button>

                    <button
  className="info"
  onClick={() =>
    navigate(`/admin/admin-events/${e.id}/users`, {
  state: { eventTitle: e.title }
})
  }
>
  More
</button>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      )}

    </div>

  );

};


export default AdminEventPage;